import {BasicCustomCursor} from "@goodgamestudios/core";

import {BasicComboboxComponent} from "../../../basic/view/BasicComboboxComponent";
import {instanceOfClass} from "@goodgamestudios/createjs-utilities";
import {Poker2LayoutManager} from "../Poker2LayoutManager";
import {BasicComboboxEvent} from "../../../basic/event/BasicComboboxEvent";
import DisplayObject = createjs.DisplayObject;
import Container = createjs.Container;
import MovieClip = createjs.MovieClip;
import MouseEvent = createjs.MouseEvent;
import TextField = createjs.TextField;

export class Poker2DynamicCombobox extends BasicComboboxComponent {

    private isExpanded: boolean; // the combo box is expanded or not
    private _numItems: number;
    private bgMiddleNormal: any;
    private bgMiddleSelected: any;

    constructor(disp: MovieClip, defaultString: string = "", numItems: number = 0) {
        super(
            disp,
            instanceOfClass(disp, "BasicDynamicCombobox_Big") ? Library.PokerInterface.BasicComboboxItem_Big : Library.PokerInterface.BasicComboboxItem,
            defaultString,
            1,
            20,
            14
        );

        this.isExpanded = false;
        this._numItems = numItems;

        // prepare the backgrounds
        this.setMiddleBackgrounds();
        this.updateItemBGs(numItems);
        this._disp.bg.gotoAndStop(1);
        disp.bg.mouseChildren = false;

        disp.btn_arrow.mouseChildren = false;
        disp.btn_arrow.mouseEnabled = false;

        this.updateAllTextFields();
    }

    public updateItemBGs(numItems: number): void {
        if (numItems == 0) {
            return;
        }

        this._numItems = numItems;

        this._disp.bg.gotoAndStop(5);
        this.fillBackgrounds(this.bgMiddleSelected);
        this._disp.bg.gotoAndStop(4);
        this.fillBackgrounds(this.bgMiddleNormal);
        this._disp.bg.gotoAndStop(1);
    }

    protected get itemClass(): any {
        if (instanceOfClass(this._disp, "BasicDynamicCombobox_Big"))
            return Library.PokerInterface.BasicComboboxItem_Big;
        else
            return Library.PokerInterface.BasicComboboxItem;
    }

    public set defaultString(value: string) {
        this._defaultString = value;
        this._disp.txt_selected.text = value;
    }

    protected onClickArrow(): void {
        if (!this.isExpanded) {
            this.hideItems();
            this._disp.bg.gotoAndStop(2); // selected state - closed
        }
        else {
            this.showItems();
            this._disp.bg.gotoAndStop(5); // selected state - expanded
            this.toggleShowHideBackgrounds(this.bgMiddleSelected, true);
        }
    }

    private fillBackgrounds(itemTypeBG: any): void {
        let startYPos = this._disp.bg.mc_top.y + this._disp.bg.mc_top.height;
        let itemBG: DisplayObject;
        for (var i: number = 0; i <= this._numItems; i++) {
            itemBG = new itemTypeBG();
            itemBG.x = this._disp.bg.mc_top.x;
            itemBG.y = startYPos;
            this._disp.bg.addChild(itemBG);
            itemBG.visible = false;

            startYPos += itemBG.height - 2; // -2 to fill the gaps between bg parts

            // add additional background to fill the last gap
            if (i == this._numItems) {
                itemBG.scaleY /= 2;
                itemBG.y -= itemBG.height / 2; // move up, coz the scale moves it
            }
        }

        this._disp.bg.mc_bottom.y = startYPos;
    }

    private setMiddleBackgrounds(): void {
        if (instanceOfClass(this._disp, "BasicDynamicCombobox_Big")) {
            this.bgMiddleNormal = Library.PokerInterface.ComboboxItemMiddleBG_Normal_Big;
            this.bgMiddleSelected = Library.PokerInterface.ComboboxItemMiddleBG_Selected_Big;
        }
        else {
            this.bgMiddleNormal = Library.PokerInterface.ComboboxItemMiddleBG_Normal;
            this.bgMiddleSelected = Library.PokerInterface.ComboboxItemMiddleBG_Selected;
        }
    }

    private toggleShowHideBackgrounds(itemTypeBG: any, show: boolean): void {
        let itemBG: DisplayObject;
        for (var i: number = 0; i < this._disp.bg.children.length; i++) {
            itemBG = this._disp.bg.children[i];
            if (itemBG instanceof itemTypeBG) {
                itemBG.visible = show;
            }
        }

        if (this._disp.bg.mc_top && this._disp.bg.mc_bottom) {
            this._disp.bg.mc_top.visible = show;
            this._disp.bg.mc_bottom.visible = show;

            // update the bottom element
            this._disp.bg.mc_bottom.y = itemBG.y + itemBG.height;
        }
    }

    public clearItems(): void {
        let itemBG: DisplayObject;
        for (var i: number = this._disp.bg.children.length - 1; i >= 0; i--) {
            itemBG = this._disp.bg.children[i];
            if (itemBG instanceof this.bgMiddleNormal || itemBG instanceof this.bgMiddleSelected) {
                this._disp.bg.removeChild(itemBG);
            }
        }

        this.isExpanded = false;
        super.clearItems();
    }

    public hideItems(): void {
        this.isExpanded = false;
        this.closeCombobox();
        this._disp.btn_arrow.gotoAndStop(2);
        this._disp.item_Holder.visible = false;
        this._disp.dispatchEvent(new BasicComboboxEvent(BasicComboboxEvent.COMBOBOXSELECT, 0));
    }

    protected onMouseUp(event: MouseEvent): void {
        this.isExpanded = !this.isExpanded;
        this._scaleOpen = 1;

        switch (event.target) {
            case this._disp.bg:
            case this._disp.btn_arrow:
                if (!this._disp.btn_arrow.enabled) return;
                this.onClickArrow();
                break;
        }
        if (event.target instanceof this.itemClass) {
            this.selectItemIndex(event.target.id);
            this.hideItems();
        }
    }

    private closeCombobox(): void {
        this.toggleShowHideBackgrounds(this.bgMiddleSelected, false);
        this.toggleShowHideBackgrounds(this.bgMiddleNormal, false);
        this.toggleOpenCloseComboBox(false);
    }

    protected onMouseOut(event: MouseEvent): void {
        Poker2LayoutManager.getInstance().customCursor.setCursorType(BasicCustomCursor.CURSOR_ARROW);
        if (event.target instanceof this.itemClass) {
            event.target.bg.gotoAndStop(1);
        }
        else {
            this.toggleOpenCloseComboBox(false);
        }
    }

    protected onMouseOver(event: MouseEvent): void {
        Poker2LayoutManager.getInstance().customCursor.setCursorType(BasicCustomCursor.CURSOR_CLICK);
        if (event.target instanceof this.itemClass) {
            event.target.bg.gotoAndStop(2);
        }
        else {
            this.toggleOpenCloseComboBox(true);
        }
    }

    private toggleOpenCloseComboBox(selected: boolean): void {
        if (selected) { // the mouse is over the combo box
            if (this.isExpanded) {
                this.toggleShowHideBackgrounds(this.bgMiddleNormal, false);
                this._disp.bg.gotoAndStop(5);
                this.toggleShowHideBackgrounds(this.bgMiddleSelected, true);
            }
            else {
                this._disp.bg.gotoAndStop(2);
            }
        }
        else {
            if (this.isExpanded) {
                this.toggleShowHideBackgrounds(this.bgMiddleSelected, false);
                this._disp.bg.gotoAndStop(4);
                this.toggleShowHideBackgrounds(this.bgMiddleNormal, true);
            }
            else {
                this._disp.bg.gotoAndStop(1);
            }
        }
    }

    public updateAllTextFields(specificMc: Container = null): void {
        var container: Container = this.disp as Container;
        container.changeTextFieldCacheScale(2);
    }

    public addItem(data: any): void {
        super.addItem(data);
        this.updateAllTextFields();
    }

    /**
     * Search recursively for textfields
     */
    protected findTextFields(container: Container): TextField[] {
        var result: TextField[] = [];
        for (var i: number = 0; i < container.numChildren; i++) {
            var disp: DisplayObject = container.getChildAt(i);
            if (disp instanceof TextField) {
                result.push(disp);
            } else if (disp instanceof Container) {
                var childResult: any[] = this.findTextFields(disp);
                result = result.concat(childResult);
            }
        }
        return result;
    }

    /**
     * TRANSPILER GENERATED FUNCTION: If a overriding getter is defined, the setter must be defined as well. WARNING: the defining class was not found, so the parentclass was assumed. Please check if this call is correct.
     */
    protected set itemClass(value/*:Class*/)/*:void*/ {
        Object.getOwnPropertyDescriptor(BasicComboboxComponent.prototype, "itemClass").set.call(this, value);
    }
}

